import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Fingerprint, Trash2, Plus, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAuth, type PasskeyInfo } from '../context/AuthContext';
import { Button } from './ui/button';

type Status =
  | { kind: 'idle' }
  | { kind: 'registering' }
  | { kind: 'revoking'; id: string }
  | { kind: 'success'; message: string }
  | { kind: 'error';   message: string };

export default function PasskeyManager() {
  const { t } = useTranslation();
  const { registerPasskey, listPasskeys, revokePasskey, user } = useAuth();

  const [creds, setCreds] = useState<PasskeyInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [nickname, setNickname] = useState('');

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const list = await listPasskeys();
      setCreds(list);
    } catch (err: any) {
      setStatus({ kind: 'error', message: err?.message || 'Failed to load passkeys' });
    } finally {
      setLoading(false);
    }
  }, [listPasskeys]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-clear success/error messages after a few seconds.
  useEffect(() => {
    if (status.kind !== 'success' && status.kind !== 'error') return;
    const id = setTimeout(() => setStatus({ kind: 'idle' }), 4000);
    return () => clearTimeout(id);
  }, [status]);

  const handleRegister = async () => {
    setStatus({ kind: 'registering' });
    const result = await registerPasskey(nickname.trim() || undefined);
    if (result.ok) {
      setStatus({ kind: 'success', message: t('settings.passkey.registered') });
      setNickname('');
      await refresh();
    } else {
      // user.cancel() — silent
      if (result.error?.toLowerCase?.().includes('not allowed')) {
        setStatus({ kind: 'idle' });
      } else {
        setStatus({ kind: 'error', message: result.error });
      }
    }
  };

  const handleRevoke = async (id: string) => {
    if (!window.confirm(t('settings.passkey.revokeConfirm'))) return;
    setStatus({ kind: 'revoking', id });
    const ok = await revokePasskey(id);
    if (ok) {
      setStatus({ kind: 'success', message: t('settings.passkey.revoked') });
      await refresh();
    } else {
      setStatus({ kind: 'error', message: t('settings.passkey.revokeFailed') });
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-6">
      {/* Header card: explain what a passkey is */}
      <div className="rounded-lg border bg-card p-4 flex gap-3 items-start">
        <Fingerprint className="w-5 h-5 mt-0.5 text-muted-foreground" />
        <div className="text-sm">
          <p className="font-medium">{t('settings.passkey.title')}</p>
          <p className="text-muted-foreground mt-1">{t('settings.passkey.description')}</p>
        </div>
      </div>

      {/* Add a new passkey */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <p className="text-sm font-medium">{t('settings.passkey.addNew')}</p>
        <input
          type="text"
          placeholder={t('settings.passkey.nicknamePlaceholder')}
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          maxLength={100}
          className="w-full p-2 border rounded bg-background text-sm"
        />
        <Button
          onClick={handleRegister}
          disabled={status.kind === 'registering'}
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          {status.kind === 'registering'
            ? t('settings.passkey.registering')
            : t('settings.passkey.addThisDevice')}
        </Button>
      </div>

      {/* List of existing passkeys */}
      <div className="rounded-lg border bg-card">
        <div className="p-4 border-b">
          <p className="text-sm font-medium">{t('settings.passkey.yourDevices')}</p>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-muted-foreground">{t('common.loading')}</div>
        ) : creds.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">{t('settings.passkey.none')}</div>
        ) : (
          <ul className="divide-y">
            {creds.map((c) => (
              <li key={c.credential_id} className="p-4 flex items-center justify-between gap-3">
                <div className="text-sm min-w-0">
                  <p className="font-medium truncate">
                    {c.nickname || t('settings.passkey.unnamed')}
                  </p>
                  <p className="text-muted-foreground text-xs mt-0.5">
                    {`${t('settings.passkey.added')} ${new Date(c.created_at).toLocaleDateString()}`}
                    {c.last_used_at && (
                      <>
                        {' · '}
                        {`${t('settings.passkey.lastUsed')} ${new Date(c.last_used_at).toLocaleString()}`}
                      </>
                    )}
                  </p>
                </div>
                <Button
                  onClick={() => handleRevoke(c.credential_id)}
                  disabled={status.kind === 'revoking' && status.id === c.credential_id}
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950 gap-1"
                  title={t('settings.passkey.revoke')}
                >
                  <Trash2 className="w-4 h-4" />
                  {t('settings.passkey.revoke')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Status banner */}
      {status.kind === 'success' && (
        <div className="rounded-md border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-950 p-3 text-sm flex items-center gap-2 text-green-800 dark:text-green-200">
          <CheckCircle2 className="w-4 h-4" />
          {status.message}
        </div>
      )}
      {status.kind === 'error' && (
        <div className="rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950 p-3 text-sm flex items-center gap-2 text-red-800 dark:text-red-200">
          <AlertTriangle className="w-4 h-4" />
          {status.message}
        </div>
      )}
    </div>
  );
}
